import React, { useEffect, useState } from 'react';
import useFetch from './common/useFetch.jsx';
import ProductCarousel from './common/productCarousel.jsx';
import JoinClassroom from './common/JoinClassroomButton.jsx';
import CreateClassroomButton from './common/CreateClassroomButton.jsx';
import GotoAllClassrooms from './common/GotoAllClassroomsButton.jsx';
import ResponseErrorMessage from './common/ResponseErrorMessage.jsx';
import PropTypes from 'prop-types';
import MyClassroomsSkeleton from './skeletons/MyClassroomsSkeleton.jsx';
import ResponseEmptyMessageMessage from './common/ResponseEmptyMessage.jsx';

/**
 * Generates the My Classrooms component
 *
 * @returns {JSX.Element}
 * @constructor
 */
const MyClassrooms = (props) => {
	const { user } = props;
	const [classroomData, setClassroomData] = useState(null);
	const getClassroomsServices = '/internal/dynamic-home-api/Ajax/Ajax.php?action=GetClassrooms';
	const { response, loading, error } = useFetch(getClassroomsServices);
	let productLabel, image, productTitle, createDate, classrooms;

	/**
	 * Iterates the classrooms and calls the ProductCarousel component
	 *
	 * @returns {boolean}
	 */
	const generateClassrooms = () => {
		// Options for Slick Carousel
		const options = {
			variableWidth: true,
			infinite: false,
			slidesToShow: 7,
			slidesToScroll: 1,
			speed: 500,
			swipeToSlide: true,
			responsive: [
				{
					breakpoint: 4800,
					settings: 'unslick'
				},
				{
					breakpoint: 1024,
					settings: {
						slidesToShow: 2,
						slidesToScroll: 1
					}
				},
				{
					breakpoint: 576,
					settings: {
						slidesToShow: 1,
						slidesToScroll: 1
					}
				}
			]
		};

		return classroomData !== null && classroomData.map((item, key)=> {
			return (
				<div key={key} className={'my-classrooms-box'}>
					<div className={'my-classrooms-title-box'}>
						<h3 className={'artz'} data-id={item[1].creationTimestampPretty}>{item[1].title}</h3>
					</div>
					<div className={'my-classrooms-slider-box'}>
						<ProductCarousel
							options={options}
							error={error}
							loading={loading}
							data={item[0]}
							alt={item[1].title} />
					</div>
				</div>
			);
		});
	};

	/**
	 * Displays the component skeleton or and empty response if data is not available
	 *
	 * @returns {boolean|JSX.Element}
	 */
	const getMyClassrooms = () => {
		if (loading) {
		    classrooms = <MyClassroomsSkeleton />;
		} else if (classroomData !== null && !classroomData.length) {
			classrooms = <ResponseEmptyMessageMessage />;
		} else {
			classrooms = generateClassrooms();
		}

		return classrooms;
	};

	/**
	 * Listens for changes in response, loading, and error states.
	 *
	 * Calls the setClassroomData method only when response is not null and loading has finished.
	 */

	useEffect(() => {
		if (response !== null && !loading) {
			setClassroomData(
				response.payload.classrooms.reduce((myClassrooms, line)=> {
					const myProducts = Object.entries(line).reduce((myClassroomsProducts, lineItem)=> {
						if (lineItem[0] === 'products') {
							lineItem[1].reduce((accProduct, productItem)=> {
								const productLine = Object.entries(productItem);

								myClassroomsProducts = [
									...myClassroomsProducts,
									{
										uuid: productLine[0][0],
										title: productLine[0][1].title,
										url: productLine[0][1].url,
										image: productLine[0][1].image,
										productType: productLine[0][1].productType
									}
								];

								return accProduct;
							}, []);
						} else if (lineItem[0] === 'overview' || lineItem[0] === 'gradebook') {
							switch (lineItem[0]) {
							case 'overview':
								productLabel = 'Overview';
								image = 'https://media1.shmoop.com/heartbeat/overview.svg';
								break;
							case 'gradebook':
								productLabel = 'Gradebook';
								image = 'https://media1.shmoop.com/heartbeat/gradebook.svg';
								break;
							}

							myClassroomsProducts = [
								...myClassroomsProducts,
								{
									uuid: lineItem[0],
									title: productLabel,
									url: lineItem[1],
									image: image,
									productType: lineItem[0]
								}
							];
						} else {
							if (lineItem[0] === 'title') {
								productTitle = lineItem[1];
							} else if (lineItem[0] === 'creationTimestampPretty') {
								createDate = lineItem[1];
							}
						}

						return myClassroomsProducts;
					}, []);

					const readyData = [
						myProducts,
						{
							title: productTitle,
							creationTimestampPretty: createDate
						}
					];

					myClassrooms = [ ...myClassrooms, { ...readyData }];
					return myClassrooms;
				}, [])
			);
		}
	}, [response, loading, error]);

	return (
		<div className={'my-classrooms row'}>
			<div className={'my-classrooms-wrapper col-11 col-offset-1'}>
				<h2 className={'title-line artz text-white'}>My Classrooms</h2>
				<ul className={'my-classroom-buttons'}>
					<li>{ <GotoAllClassrooms btnClass={'btn btn-primary go-to-classrooms'} disabled={loading} /> }</li>
					{
						user.type === 'student' ?
							<li>
								<JoinClassroom className={'classroom-join-modal'} user={user} disabled={loading} />
							</li> :
							<li>
								<CreateClassroomButton btnClass={'btn btn-primary'} disabled={loading} />
							</li>
					}
				</ul>
				{ error !== null ? <ResponseErrorMessage error={error} /> : getMyClassrooms() }
			</div>
		</div>
	);
};

export default MyClassrooms;

MyClassrooms.propTypes = {
	user: PropTypes.object
};
